import React, { useMemo } from "react";
import { Pencil, Split, RotateCcw, ExternalLink } from "lucide-react";
import { money2, suggestedCommissionForJob, needsCommissionReversal } from "../payrollHelpers";

const STATUS_PILL = {
  Completed: "bg-emerald-50 text-emerald-700",
  Canceled: "bg-rose-50 text-rose-700",
  "In Progress": "bg-blue-50 text-blue-700",
};

export default function CommissionsTab({
  row, invoices, jobs, pricebook, adjustments, canPayroll,
  onEditCommission, onSplitCommission, onToggleInclude, onViewCustomer, isLocked,
}) {
  const invoiceByJobId = useMemo(() => {
    const m = new Map();
    (invoices || []).forEach((iv) => { if (iv.jobId) m.set(iv.jobId, iv); });
    return m;
  }, [invoices]);
  const jobById = useMemo(() => {
    const m = new Map();
    (jobs || []).forEach((j) => m.set(j.id, j));
    return m;
  }, [jobs]);
  const pricebookById = useMemo(() => {
    const m = new Map();
    (pricebook || []).forEach((p) => m.set(p.id, p));
    return m;
  }, [pricebook]);
  const commissionAdjByJob = useMemo(() => {
    const m = {};
    (adjustments || []).filter((a) => a.category === "COMMISSION" && a.jobId).forEach((a) => {
      (m[a.jobId] = m[a.jobId] || []).push(a);
    });
    return m;
  }, [adjustments]);

  const canEdit = (canPayroll("edit_commissions") || canPayroll("add_adjustments")) && !isLocked;
  // Fold in any COMMISSION adjustment whose jobId isn't one of this tech's own
  // jobLines (e.g. they're the RECEIVER of a split from another tech's job) —
  // otherwise their share is only visible in the Adjustments tab, not here.
  const lines = useMemo(() => {
    const base = row.jobLines || [];
    const ownJobIds = new Set(base.map((jl) => jl.jobId));
    const extraRows = Object.keys(commissionAdjByJob)
      .filter((jobId) => !ownJobIds.has(jobId))
      .map((jobId) => {
        const invoice = invoiceByJobId.get(jobId);
        const job = jobById.get(jobId);
        return {
          jobId,
          customer: invoice?.customer || job?.customer || "—",
          customerId: invoice?.customerId || job?.customerId || null,
          status: invoice?.status || job?.status || "—",
          invoiceAmount: invoice ? Number(invoice.total) || 0 : Number(job?.value) || 0,
          commissionPct: 0,
          commissionAmount: 0,
          isCompleted: true,
          included: true,
          isSynthetic: true,
        };
      });
    return [...base, ...extraRows];
  }, [row.jobLines, commissionAdjByJob, invoiceByJobId, jobById]);

  return (
    <div data-testid="payroll-commissions-tab">
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              <th className="px-2 py-1.5 text-left">Job #</th>
              <th className="px-2 py-1.5 text-left">Customer</th>
              <th className="px-2 py-1.5 text-left">Status</th>
              <th className="px-2 py-1.5 text-right">Sale Amount</th>
              <th className="px-2 py-1.5 text-right">System %/$</th>
              <th className="px-2 py-1.5 text-right">Adjustment</th>
              <th className="px-2 py-1.5 text-right">Final</th>
              <th className="px-2 py-1.5 text-center">Eligible</th>
              <th className="px-2 py-1.5 text-right min-w-[90px] whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.map((jl) => {
              const invoice = invoiceByJobId.get(jl.jobId);
              const suggested = (invoice && !jl.isSynthetic) ? suggestedCommissionForJob(jl, invoice, row.pay, pricebookById) : null;
              const jobAdjustments = commissionAdjByJob[jl.jobId] || [];
              const adjTotal = jobAdjustments.reduce((s, a) => s + a.adjustmentAmount, 0);
              const systemAmount = jl.included ? Number(jl.commissionAmount) || 0 : 0;
              const finalAmount = systemAmount + adjTotal;
              const hasReversal = jobAdjustments.some((a) => a.source === "reversal");
              const needsReversal = needsCommissionReversal(jl, invoice, jobAdjustments);

              return (
                <tr key={jl.jobId} className={jl.included ? "" : "opacity-60"} data-testid={`commission-jobline-${jl.jobId}`}>
                  <td className="px-2 py-1.5 font-medium text-slate-700">{jl.jobId}</td>
                  <td className="px-2 py-1.5">
                    <button type="button" onClick={() => jl.customerId && onViewCustomer(jl.customerId)} disabled={!jl.customerId} className="text-blue-600 hover:underline font-medium disabled:text-slate-500 disabled:no-underline">
                      {jl.customer}
                    </button>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS_PILL[jl.status] || "bg-slate-100 text-slate-600"}`}>{jl.status}</span>
                    {jl.isSynthetic && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600" data-testid={`commission-split-share-badge-${jl.jobId}`}>Split share</span>}
                    {needsReversal && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Needs reversal</span>}
                    {hasReversal && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Reversed</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{money2(jl.invoiceAmount)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-500">
                    {jl.commissionPct ? `${jl.commissionPct}%` : "—"} / {money2(systemAmount)}
                    {suggested?.hasProductRule && Math.abs(suggested.amount - systemAmount) > 0.01 && (
                      <div className="text-[10px] text-indigo-600 font-semibold">Suggested: {suggested.pct}% ({money2(suggested.amount)})</div>
                    )}
                  </td>
                  <td className={`px-2 py-1.5 text-right ${adjTotal > 0 ? "text-emerald-600" : adjTotal < 0 ? "text-rose-600" : "text-slate-300"}`}>{adjTotal !== 0 ? money2(adjTotal) : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-semibold">{money2(finalAmount)}</td>
                  <td className="px-2 py-1.5 text-center">
                    {jl.isCompleted ? (
                      <span className="text-emerald-600 text-[11px] font-semibold">Included</span>
                    ) : (
                      <input type="checkbox" checked={!!jl.included} disabled={isLocked} onChange={() => onToggleInclude(jl.jobId)} data-testid={`commission-include-${jl.jobId}`} className="h-3.5 w-3.5 accent-blue-600" />
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {canEdit && (
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => onEditCommission(jl, systemAmount, false)} data-testid={`commission-edit-${jl.jobId}`} title="Edit commission" className="h-6 w-6 rounded border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><Pencil className="h-3 w-3 text-slate-500" /></button>
                        <button type="button" onClick={() => onSplitCommission(jl, systemAmount)} data-testid={`commission-split-${jl.jobId}`} title="Split commission" className="h-6 w-6 rounded border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><Split className="h-3 w-3 text-slate-500" /></button>
                        {needsReversal && (
                          <button type="button" onClick={() => onEditCommission(jl, finalAmount, true)} data-testid={`commission-reverse-${jl.jobId}`} title="Reverse commission" className="h-6 w-6 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 flex items-center justify-center"><RotateCcw className="h-3 w-3 text-amber-700" /></button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {lines.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-6 text-center text-[12px] text-slate-400 italic">No jobs for this tech in the selected range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-slate-400 italic flex items-center gap-1">
        <ExternalLink className="h-3 w-3" /> Editing here never changes the original job or invoice — every change creates a reasoned, audited payroll adjustment.
      </div>
    </div>
  );
}
